/**
 * Review draft's pop-up on a prompt suggestion (PROC-266): the prompt's other
 * open suggestions, one full card at a time as on Eval Results, each
 * incorporated into this one or dismissed, so they ship in its PR instead of
 * waiting on it. Incorporating keeps you here to test the combined draft; Rio
 * writes it when the drafts change the same field, and the page polls.
 * Dismissing only goes on to Review draft.
 */
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";

import { Button, Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  type PromptSuggestion,
  getGetPromptSuggestionQueryKey,
  getListPromptSuggestionsQueryKey,
  useGetPromptSuggestion,
  useIncorporatePromptSuggestions,
} from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { SuggestionDetails } from "@pages/Evaluations/PromptSuggestions";
import {
  IncorporateChoice,
  type IncorporateChoiceValue,
  incorporateRequest,
  suggestionDraft,
} from "@pages/Evaluations/promptSuggestionFields";
import { useSuggestionCatalogs } from "@pages/Evaluations/useSuggestionCatalogs";

const CHOICE_LABELS: Record<IncorporateChoiceValue, string> = {
  [IncorporateChoice.INCORPORATE]: "Incorporate into this draft",
  [IncorporateChoice.DISMISS]: "Dismiss",
};

/** One other suggestion's full card. */
const OtherSuggestion: FC<{ suggestionId: number }> = ({ suggestionId }) => {
  const { data, isLoading } = useGetPromptSuggestion(suggestionId);
  const other = data?.status === 200 ? data.data : null;
  const drafts = useMemo(() => (other ? [suggestionDraft(other)] : []), [other]);
  const { catalogs, catalogsPending } = useSuggestionCatalogs(drafts);
  if (!other) {
    return (
      <Paragraph size="small" colorType="secondary">
        {isLoading ? "Loading suggestion…" : "Couldn't load this suggestion."}
      </Paragraph>
    );
  }
  return (
    <div className="flex flex-col gap-y-4">
      <SuggestionDetails
        suggestion={other}
        draft={drafts[0]}
        catalogs={catalogs}
        catalogsPending={catalogsPending}
        orgName={other.organization_name}
      />
    </div>
  );
};

interface IncorporateSuggestionsDialogProps {
  suggestion: PromptSuggestion;
  onClose: () => void;
  /** Go on to Review draft: nothing was incorporated. */
  onReview: () => void;
}

export const IncorporateSuggestionsDialog: FC<IncorporateSuggestionsDialogProps> = ({
  suggestion,
  onClose,
  onReview,
}) => {
  const queryClient = useQueryClient();
  const mutation = useIncorporatePromptSuggestions();
  const [choices, setChoices] = useState<Record<number, IncorporateChoiceValue | undefined>>({});
  const [index, setIndex] = useState(0);
  const others = suggestion.incorporable;
  const request = incorporateRequest(others, choices);
  const current = others[Math.min(index, others.length - 1)];

  const choose = (choice: IncorporateChoiceValue) => {
    setChoices((prev) => ({ ...prev, [current.id]: choice }));
    if (index < others.length - 1) setIndex(index + 1);
  };

  const submit = async () => {
    if (!request) return;
    try {
      const resp = await mutation.mutateAsync({ suggestionId: suggestion.id, data: request });
      if (resp.status !== 200 && resp.status !== 202) {
        notifyError(resp.data.detail);
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetPromptSuggestionQueryKey(suggestion.id) }),
        queryClient.invalidateQueries({ queryKey: getListPromptSuggestionsQueryKey() }),
      ]);
      onClose();
      if (request.incorporate_ids.length === 0) {
        onReview();
      } else if (resp.status === 202) {
        notifySuccess("Rio is combining the suggestions into this draft. Test it once it's ready.");
      } else {
        notifySuccess("Incorporated into this draft. Test it, then review again.");
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Failed to incorporate the suggestions.");
    }
  };

  return (
    <Dialog
      open
      setOpen={(open) => {
        if (!open) onClose();
      }}
      title={`Other suggestions for this prompt · ${index + 1} of ${others.length}`}
      size="6xl"
      primaryButton={{
        text: mutation.isPending ? "Saving…" : "Continue",
        onClick: () => void submit(),
        disabled: request === null || mutation.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose, disabled: mutation.isPending }}
    >
      <div className="flex flex-col gap-y-4">
        <Paragraph size="small" colorType="secondary">
          Incorporate each into this draft so they ship in one PR, or dismiss it. Incorporating changes the draft, so
          you&apos;ll test it again before reviewing.
        </Paragraph>
        {/* Only the card scrolls: the title, choices and Continue stay in view. */}
        <div className="max-h-[55vh] overflow-y-auto pr-1">
          <OtherSuggestion key={current.id} suggestionId={current.id} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-4">
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              aria-label="Previous suggestion"
              onClick={() => setIndex(index - 1)}
              disabled={index === 0}
              className="rounded-md border border-border-medium p-1 text-text-secondary disabled:opacity-40"
            >
              <ChevronLeftIcon className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next suggestion"
              onClick={() => setIndex(index + 1)}
              disabled={index >= others.length - 1}
              className="rounded-md border border-border-medium p-1 text-text-secondary disabled:opacity-40"
            >
              <ChevronRightIcon className="size-4" />
            </button>
            <span className="text-xs text-text-secondary">
              {Object.values(choices).filter(Boolean).length} of {others.length} decided
            </span>
          </div>
          <div role="radiogroup" aria-label={current.theme_title} className="flex gap-x-2">
            {Object.values(IncorporateChoice).map((choice) => (
              <Button
                key={choice}
                role="radio"
                aria-checked={choices[current.id] === choice}
                text={CHOICE_LABELS[choice]}
                variant={choice === IncorporateChoice.INCORPORATE ? "primary" : "secondary"}
                buttonStyle={choices[current.id] === choice ? "filled" : "border"}
                size="sm"
                onClick={() => choose(choice)}
              />
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
